import * as React from 'react';
import { faCheckCircle, faCircle, faCircleExclamation } from '@fortawesome/pro-regular-svg-icons';

import { FingerIcon } from '../../Finger/FingerIcon';
import { FingerExpander } from '../../Finger/FingerExpander';
import { RiskIcon } from '../../RiskAction/RiskIcon';
import { RiskAlert } from '../../RiskAction/RiskAlert';
import { Span } from 'shared-components';

export enum Status {
  Todo = 'Todo',
  InProgress = 'InProgress',
  Complete = 'Complete',
  InError = 'InError',
  AtRisk = 'AtRisk',
  Locked = 'Locked',
}

interface Props {
  id?: string;
  status: Status;
  label: string;
  canCheckOff?: boolean;
  isCheckedOff?: boolean;
  onCheckOffRisk?: () => void;
  children?: React.ReactNode;
}

function Finger({
  id = 'finger',
  status,
  label,
  isCheckedOff = false,
  canCheckOff = false,
  onCheckOffRisk,
  children,
}: Props) {
  function checkOffRisk() {
    if (onCheckOffRisk) onCheckOffRisk();
  }

  const getIconProps = () => {
    switch (status) {
      case Status.Todo:
      case Status.InProgress:
        return {
          icon: faCircle,
          color: 'gray',
          label: 'Item has not been completed yet',
        };
      case Status.InError:
        return {
          icon: faCircleExclamation,
          color: 'red',
          tooltip: 'There is an error with the data',
        };
      case Status.Locked:
        return {
          icon: faCheckCircle,
          color: 'green',
          label: 'Item has been completed and is now locked',
        };
      case Status.AtRisk:
      case Status.Complete:
        return {
          icon: faCheckCircle,
          color: 'green',
          label: 'Item has been completed',
        };
    }
  };

  const Icon = () => {
    const { label, color, icon } = getIconProps();
    return <FingerIcon label={label} color={color} icon={icon} />;
  };

  const Flag = () => {
    if (status === Status.AtRisk) {
      return (
        <Span data-selector={`${id}-risk-status-icon`}>
          <RiskIcon
            id={id}
            isCheckedOff={isCheckedOff}
            canCheckOff={canCheckOff}
            onCheckOffRisk={checkOffRisk}
            boxProps={{ ml: 2 }}
          />
        </Span>
      );
    }
    return null;
  };

  const Alert = () => {
    if (status === Status.AtRisk) {
      return <RiskAlert id={id} isCheckedOff={isCheckedOff} canCheckOff={canCheckOff} onCheckOffRisk={checkOffRisk} />;
    }
  };

  return (
    <FingerExpander id={id} label={label} icon={<Icon />} flag={<Flag />} alert={<Alert />}>
      {children}
    </FingerExpander>
  );
}

export default Finger;
